<template>
  <v-menu
    v-model="shown"
    :close-on-content-click="false"
    offset-y
  >
    <template v-slot:activator="{ attrs, on }">
      <div class="granularity-dropdown">
        <v-btn
          class="granularity-dropdown__button"
          text
          v-bind="attrs"
          v-on="on"
        >
          {{ selectedGranularity.label }}
        </v-btn>
      </div>
    </template>

    <v-list>
      <div
        v-for="granularity in availableGranularities"
        :key="granularity.label"
        v-tooltip.top-end="getTooltip(granularity)"
      >
        <v-list-item
          :disabled="granularity.disable"
          link
          :class="{
            'primary--text v-list-item--active': selectedGranularity.label === granularity.label
          }"
          @click="setGranularity(granularity)"
        >
          <v-list-item-title
            v-text="getLabel(granularity)"
          />
        </v-list-item>
      </div>
    </v-list>
  </v-menu>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import cardsModel from '@/mixins/cards-model';
import { GRANULARITY } from '@/services/charts-service/constants';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { availableGranularities, getUsage, minViableGranularity } from '@/utils/granularity';

const autoLabel = 'Auto';

export default {
  name: 'GranularityDropDown',
  mixins: [
    cardsModel,
  ],
  props: {
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      shown: false,
      selected: null,
    };
  },
  computed: {
    ...mapState('filter', [
      'dateRange',
    ]),
    ...mapActions('dashboard', [
      'saveGranularity',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    availableGranularities() {
      return availableGranularities();
    },
    selectedGranularity: {
      get() {
        if (!this.selected || this.isEditor) {
          return getUsage(this.card?.datePortion);
        }

        return this.selected;
      },
      set(granularity) {
        this.shown = false;
        this.selected = granularity;

        if (this.isEditor) {
          this.saveSelectedDatePortion(granularity);
        }

        trackEventWithUserProperties('ST Dashboard DateGroup Edit', { DateGroup: granularity.label, DateFilter: this.dateRange.period });

        this.$emit('reload', { granularity });
      },
    },
  },
  methods: {
    setGranularity(granularity) {
      this.selectedGranularity = granularity;
    },
    getLabel(granularity) {
      const viableGranularity = minViableGranularity(this.getDiffDatesInMinutes);

      if (GRANULARITY.AUTO.label === granularity.label) {
        GRANULARITY.AUTO = { ...viableGranularity };
        GRANULARITY.AUTO.label = `${autoLabel} (${viableGranularity.label})`;
      }

      return granularity.label;
    },
    getTooltip(granularity) {
      if (granularity.disable) {
        return granularity.tooltip;
      }
    },
    saveSelectedDatePortion(granularity) {
      this.card.datePortion = granularity.label.includes(autoLabel) ? null : granularity.datePortion;
      this.saveCards();
    },
  },
};
</script>

<style scoped lang="scss">
.granularity-dropdown {
  display: flex;
  justify-content: flex-end;
  min-width: 96px;
  margin-right: var(--ds-spacing-inline-xs);

  &__button {
    position: relative;
    min-width: auto !important;
    height: 24px !important;
    padding: 0 24px 0 var(--ds-spacing-stack-quark) !important;
    font-size: var(--ds-font-size-xs);
    line-height: var(--ds-font-line-height-dense);
    text-align: left;
    text-transform: none;

    &::after {
      position: absolute;
      top: 50%;
      right: var(--ds-spacing-stack-nano);
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid var(--ds-color-gray-2);
      content: '';
    }
  }
}

::v-deep {
  .v-btn__content {
    text-align: left;
    justify-content: left;
  }
}
</style>
